import React, { useState } from 'react';
import { Modal, FormField, Button, IconButton, inputCls, NumberField, ChoiceKeys, DatePicker } from '../ui';
import { useConfirm } from '../../hooks/useConfirm';
import db from '../../lib/database';

// ===== TRUCK SERVICE MODAL (sql/075, 2026-08-03) =====
// Per-truck maintenance panel opened from the Fleet page's Service
// button: tag/registration expiration (saved onto the truck row), an
// add-service form (type keys via ChoiceKeys — form-controls.md standard),
// and the service history with per-entry delete. "Next due" is optional
// by date and/or miles; the date drives the Fleet page's due badges.

const SERVICE_TYPES = ['Oil Change', 'Brakes', 'Tires', 'Inspection', 'Repair', 'Other'];

const emptyEntry = { service_type: 'Oil Change', service_date: '', odometer_miles: '', cost: '', notes: '', next_due_date: '', next_due_miles: '' };

export default function TruckServiceModal({ truck, entries, onClose, onSaveTruck, onChanged, showToast }) {
    const { confirm, confirmElement } = useConfirm();
    const [tagExp, setTagExp] = useState(truck.tag_expiration || '');
    const [savingTag, setSavingTag] = useState(false);
    const [form, setForm] = useState({ ...emptyEntry, service_date: new Date().toLocaleDateString('en-CA') });
    const [customType, setCustomType] = useState('');
    const [saving, setSaving] = useState(false);
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    // "Needs service" open-issue flag (sql/076). Button-styled toggle —
    // no native checkbox (Tailwind-CDN quirk, CLAUDE.md).
    const [needsService, setNeedsService] = useState(!!truck.needs_service);
    const [issueText, setIssueText] = useState(truck.service_issue || '');
    const [savingIssue, setSavingIssue] = useState(false);
    const [resolveOnLog, setResolveOnLog] = useState(true);
    const saveIssue = async (nextFlag = needsService, nextText = issueText) => {
        setSavingIssue(true);
        try {
            await onSaveTruck({ ...truck, tag_expiration: tagExp || null, needs_service: nextFlag, service_issue: nextFlag ? (nextText.trim() || null) : null });
            showToast(nextFlag ? 'Truck flagged — needs service' : 'Service flag cleared');
        } catch (e) {
            showToast('Error: ' + e.message, 'error');
        } finally { setSavingIssue(false); }
    };

    const saveTag = async () => {
        setSavingTag(true);
        try {
            await onSaveTruck({ ...truck, tag_expiration: tagExp || null });
            showToast('Tag expiration saved');
        } catch (e) {
            showToast('Error: ' + e.message, 'error');
        } finally { setSavingTag(false); }
    };

    const addEntry = async () => {
        const type = form.service_type === 'Other' ? (customType.trim() || 'Other') : form.service_type;
        if (!form.service_date) { showToast('Service date is required', 'error'); return; }
        setSaving(true);
        try {
            await db.addTruckServiceEntry({
                truck_id: truck.id,
                service_type: type,
                service_date: form.service_date,
                odometer_miles: form.odometer_miles === '' ? null : parseInt(form.odometer_miles, 10) || null,
                cost: form.cost === '' ? null : parseFloat(form.cost) || null,
                notes: form.notes.trim() || null,
                next_due_date: form.next_due_date || null,
                next_due_miles: form.next_due_miles === '' ? null : parseInt(form.next_due_miles, 10) || null,
            });
            // Logging the fix can clear the open needs-service flag in
            // the same motion (checkbox, default on).
            if (needsService && resolveOnLog) {
                setNeedsService(false);
                setIssueText('');
                await saveIssue(false, '');
            }
            setForm({ ...emptyEntry, service_date: new Date().toLocaleDateString('en-CA') });
            setCustomType('');
            showToast('Service logged');
            onChanged?.();
        } catch (e) {
            showToast('Error: ' + e.message, 'error');
        } finally { setSaving(false); }
    };

    const removeEntry = async (entry) => {
        if (!(await confirm({ title: `Delete ${entry.service_type} on ${entry.service_date}?`, body: `This entry is removed from ${truck.name}'s service history. The truck and its other entries are kept. This can't be undone.`, confirmLabel: 'Delete entry', danger: true }))) return;
        try {
            await db.deleteTruckServiceEntry(entry.id);
            showToast('Entry deleted');
            onChanged?.();
        } catch (e) {
            showToast('Error: ' + e.message, 'error');
        }
    };

    const fmtDate = (iso) => iso ? new Date(iso + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
    const todayIso = new Date().toLocaleDateString('en-CA');

    return (
        <Modal title={`Service — ${truck.name}`} onClose={onClose} width="max-w-2xl">
            <div className="space-y-5">
                {/* Needs-service flag + open issue (sql/076) */}
                <div className={`border rounded-lg p-3 ${needsService ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}>
                    <div className="flex items-center justify-between gap-3">
                        <button type="button"
                            onClick={() => {
                                const next = !needsService;
                                setNeedsService(next);
                                if (!next) saveIssue(false, '');
                            }}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-bold ${needsService ? 'bg-red-600 text-white' : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-100'}`}>
                            <span className={`inline-flex items-center justify-center w-4 h-4 rounded border-2 ${needsService ? 'bg-white border-white' : 'border-slate-400'}`}>
                                {needsService && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>}
                            </span>
                            Needs service
                        </button>
                        {needsService && <span className="text-[11px] font-bold text-red-600">Shown on the Fleet card until cleared</span>}
                    </div>
                    {needsService && (
                        <div className="mt-2.5 flex items-end gap-2">
                            <FormField label="Describe the issue">
                                <textarea className={inputCls} rows="2" value={issueText} onChange={e => setIssueText(e.target.value)} placeholder="Brakes grinding, front right · check engine light on…" />
                            </FormField>
                            <Button loading={savingIssue} onClick={() => saveIssue(true, issueText)}>Save</Button>
                        </div>
                    )}
                </div>

                {/* Tag / registration */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                    <div className="flex items-end gap-3 flex-wrap">
                        <FormField label="Tag / registration expires">
                            <DatePicker value={tagExp} onChange={v => setTagExp(v)} placeholder="Pick a date…" />
                        </FormField>
                        <Button loading={savingTag} onClick={saveTag}>Save</Button>
                        {tagExp && tagExp < todayIso && <span className="text-xs font-bold text-red-600 pb-3">⚠ Expired</span>}
                    </div>
                </div>

                {/* Add service */}
                <div>
                    <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2">Log Service</div>
                    <div className="flex flex-wrap items-center gap-1.5 mb-3">
                        <ChoiceKeys items={SERVICE_TYPES.map(t => ({ value: t, label: t }))} value={form.service_type} onChange={v => set('service_type', v)} ariaLabel="Service type" />
                        {form.service_type === 'Other' && (
                            <input className={`${inputCls} !w-44 text-xs`} placeholder="What was serviced?" value={customType} onChange={e => setCustomType(e.target.value)} />
                        )}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        <FormField label="Date *">
                            <DatePicker value={form.service_date} onChange={v => set('service_date', v)} placeholder="Service date…" />
                        </FormField>
                        <FormField label="Odometer (mi)">
                            <NumberField value={form.odometer_miles} onChange={v => set('odometer_miles', v)} step={100} min={0} suffix="mi" placeholder="e.g. 84200" />
                        </FormField>
                        <FormField label="Cost ($)">
                            <NumberField value={form.cost} onChange={v => set('cost', v)} step={5} min={0} prefix="$" placeholder="0.00" />
                        </FormField>
                        <FormField label="Next due (date)">
                            <DatePicker value={form.next_due_date} onChange={v => set('next_due_date', v)} placeholder="Optional…" />
                        </FormField>
                        <FormField label="Next due (miles)">
                            <NumberField value={form.next_due_miles} onChange={v => set('next_due_miles', v)} step={100} min={0} suffix="mi" placeholder="e.g. 89200" />
                        </FormField>
                        <FormField label="Notes">
                            <input className={inputCls} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Front pads, rotors turned…" />
                        </FormField>
                    </div>
                    <div className="flex items-center justify-end gap-3 mt-3">
                        {needsService && (
                            <button type="button" onClick={() => setResolveOnLog(r => !r)}
                                className="flex items-center gap-1.5 text-xs font-semibold text-slate-600">
                                <span className={`inline-flex items-center justify-center w-4 h-4 rounded border-2 ${resolveOnLog ? 'bg-emerald-600 border-emerald-600' : 'border-slate-400 bg-white'}`}>
                                    {resolveOnLog && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>}
                                </span>
                                This resolves the open issue
                            </button>
                        )}
                        <Button loading={saving} onClick={addEntry}>Add Entry</Button>
                    </div>
                </div>

                {/* History */}
                <div>
                    <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2">History ({entries.length})</div>
                    {entries.length === 0 ? (
                        <p className="text-sm text-slate-400 py-3 text-center">No service recorded yet.</p>
                    ) : (
                        <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                            {entries.map(e => (
                                <div key={e.id} className="flex items-start gap-3 border border-slate-200 rounded-lg px-3 py-2">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-sm font-bold text-slate-900">{e.service_type}</span>
                                            <span className="text-xs text-slate-500">{fmtDate(e.service_date)}</span>
                                            {e.odometer_miles != null && <span className="text-xs text-slate-500">{Number(e.odometer_miles).toLocaleString()} mi</span>}
                                            {e.cost != null && <span className="text-xs text-slate-500">${Number(e.cost).toFixed(2)}</span>}
                                        </div>
                                        {(e.next_due_date || e.next_due_miles != null) && (
                                            <div className={`text-xs mt-0.5 font-semibold ${e.next_due_date && e.next_due_date < todayIso ? 'text-red-600' : 'text-slate-600'}`}>
                                                Next due{e.next_due_date ? `: ${fmtDate(e.next_due_date)}` : ''}{e.next_due_date && e.next_due_miles != null ? ' or ' : e.next_due_miles != null ? ': ' : ''}{e.next_due_miles != null ? `${Number(e.next_due_miles).toLocaleString()} mi` : ''}
                                                {e.next_due_date && e.next_due_date < todayIso ? ' — overdue' : ''}
                                            </div>
                                        )}
                                        {e.notes && <div className="text-xs text-slate-500 mt-0.5">{e.notes}</div>}
                                    </div>
                                    <IconButton label="Delete entry" size="sm" tone="danger" onClick={() => removeEntry(e)}>✕</IconButton>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            {confirmElement}
        </Modal>
    );
}
